import { Toggle, LinkExternal01 } from "@velaro/velaro-component-lib";
import FormGroup from "@/components/FormGroup";
import * as React from "react";
import Input from "@/components/Input";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import flags from "react-phone-number-input/flags";
import Select from "@/components/Select";
import { Site } from "@/types";
import { SiteSettings } from "../types";
import useUsers from "@/hooks/useUsers";

interface GeneralSettingsProps {
  site: Site;
  setSite: React.Dispatch<React.SetStateAction<Site>>;
  siteSettings: SiteSettings;
  setSiteSettings: (updater: React.SetStateAction<SiteSettings>) => void;
  timeZoneOptions: Record<string, string>;
  countryOptions: Record<string, string>;
  isVisible: (text: string) => boolean;
}

export default function GeneralSettings({
  site,
  setSite,
  siteSettings,
  setSiteSettings,
  timeZoneOptions,
  countryOptions,
  isVisible
}: GeneralSettingsProps) {
  const { users } = useUsers();

  return (
    <div>
      <div className="mb-8">
        {isVisible("company name") && (
          <h1 className="text-lg font-medium mb-6">{site.companyName}</h1>
        )}

        {isVisible("site id created") && (
          <div className="space-y-3">
            <div className="flex items-center">
              <span className="text-gray-600 w-24">Site ID:</span>
              <span className="text-gray-900">{siteSettings?.siteId}</span>
            </div>

            <div className="flex items-center">
              <span className="text-gray-600 w-24">Total users:</span>
              <span className="text-black">{users.length}</span>
            </div>

            <div className="flex items-center">
              <span className="text-gray-600 w-24">Created:</span>
              <span className="text-black">
                {site.dateCreated
                  ? new Date(site.dateCreated).toLocaleDateString()
                  : "N/A"}
              </span>
            </div>
          </div>
        )}
      </div>

      {(isVisible("company name") || isVisible("site id created")) &&
        (isVisible("website") ||
          isVisible("address line 1 street") ||
          isVisible("address line 2 apt unit suite") ||
          isVisible("city") ||
          isVisible("state") ||
          isVisible("country") ||
          isVisible("zip code postal") ||
          isVisible("primary language english spanish french german italian") ||
          isVisible("timezone")) && (
          <div
            className="border-b mb-6"
            style={{ borderColor: "#CBD5E1" }}
          ></div>
        )}

      <div className="w-full">
        <div className="grid grid-cols-4 gap-4 mb-4">
          {isVisible("company name") && (
            <div>
              <label className="block text-sm font-normal text-gray-700 mb-2">
                Company Name
              </label>
              <Input
                value={site.companyName || ""}
                onChange={(value) => {
                  setSite((prev) => ({ ...prev, companyName: value }));
                }}
                placeholder="Enter company name"
              />
            </div>
          )}

          {isVisible("website") && (
            <div>
              <label className="block text-sm font-normal text-gray-700 mb-2">
                Website
              </label>
              <Input
                value={site.companyURL || ""}
                onChange={(value) => {
                  setSite((prev) => ({ ...prev, companyURL: value }));
                }}
                placeholder="https://example.com"
              />
            </div>
          )}

          {isVisible("address line 1 street") && (
            <div>
              <label className="block text-sm font-normal text-gray-700 mb-2">
                Address Line 1
              </label>
              <Input
                value={site.companyAddress1 || ""}
                onChange={(value) => {
                  setSite((prev) => ({ ...prev, companyAddress1: value }));
                }}
                placeholder="Street"
              />
            </div>
          )}

          {isVisible("address line 2 apt unit suite") && (
            <div>
              <label className="block text-sm font-normal text-gray-700 mb-2">
                Address Line 2
              </label>
              <Input
                value={site.companyAddress2 || ""}
                onChange={(value) => {
                  setSite((prev) => ({ ...prev, companyAddress2: value }));
                }}
                placeholder="No./Apt/Unit/Suite"
              />
            </div>
          )}
        </div>

        <div className="grid grid-cols-4 gap-4 mb-4">
          {isVisible("city") && (
            <div>
              <label className="block text-sm font-normal text-gray-700 mb-2">
                City
              </label>
              <Input
                value={site.city || ""}
                onChange={(value) => {
                  setSite(prev => ({ ...prev, city: value }));
                }}
                placeholder="Enter city"
              />
            </div>
          )}

          {isVisible("state") && (
            <div>
              <label className="block text-sm font-normal text-gray-700 mb-2">
                State
              </label>
              <Input
                value={site.region || ""}
                onChange={(value) => {
                  setSite((prev) => ({ ...prev, region: value }));
                }}
                placeholder="Enter state"
              />
            </div>
          )}

          {isVisible("country") && (
            <div>
              <label className="block text-sm font-normal text-gray-700 mb-2">
                Country
              </label>
              <Select
                options={countryOptions}
                value={site.companyCountryID?.toString() || ""}
                onChange={(value) => {
                  setSite((prev) => ({
                    ...prev,
                    companyCountryID: value ? parseInt(value) : undefined
                  }));
                }}
              />
            </div>
          )}

          {isVisible("zip code postal") && (
            <div>
              <label className="block text-sm font-normal text-gray-700 mb-2">
                Zip Code
              </label>
              <Input
                value={site.companyPostalCode || ""}
                onChange={(value) => {
                  setSite((prev) => ({ ...prev, companyPostalCode: value }));
                }}
                placeholder="Enter zip code"
              />
            </div>
          )}
        </div>

        <div className="grid grid-cols-4 gap-4 mb-4">
          {isVisible("phone number contact") && (
            <div>
              <label className="block text-sm font-normal text-gray-700 mb-2">
                Phone Number
              </label>
              <PhoneInput
                placeholder="Phone Number"
                flags={flags}
                country="US"
                className="px-3 w-full h-10 bg-white rounded-lg border border-slate-300 text-sm focus-visible:outline-none focus-visible:border-blue-600 hover:outline-none"
                value={site.phoneNumber || ""}
                onChange={(value) => {
                  setSite((prev) => ({ ...prev, phoneNumber: value || "" }));
                }}
                defaultCountry="US"
              />
            </div>
          )}
        </div>

        {(isVisible("website") ||
          isVisible("address line 1 street") ||
          isVisible("address line 2 apt unit suite") ||
          isVisible("city") ||
          isVisible("state") ||
          isVisible("country") ||
          isVisible("zip code postal") ||
          isVisible("phone number contact")) &&
          (isVisible(
            "primary language english spanish french german italian"
          ) ||
            isVisible("timezone")) && (
            <div
              className="border-b my-8"
              style={{ borderColor: "#CBD5E1" }}
            ></div>
          )}

        {isVisible(
          "primary language english spanish french german italian"
        ) && (
          <div className="w-1/2 mb-4">
            <FormGroup
              label="Primary Language"
              hint="Set default language for system messages."
              direction="label-stacked"
            >
              <Select
                options={{
                  en: "English",
                  es: "Spanish",
                  fr: "French",
                  de: "German",
                  it: "Italian"
                }}
                value={site.language || "en"}
                onChange={(value) => {
                  setSite((prev) => ({ ...prev, language: value }));
                }}
              />
            </FormGroup>
          </div>
        )}

        {isVisible("timezone") && (
          <div className="w-1/2">
            <FormGroup
              label="Timezone"
              hint="Define the global time zone for timestamps."
              direction="label-stacked"
            >
              <Select
                options={timeZoneOptions}
                value={site.timezone || siteSettings.timezone}
                onChange={(value) => {
                  setSite((prev) => ({ ...prev, timezone: value }));
                  setSiteSettings(() => {
                    return { ...siteSettings, timezone: value };
                  });
                }}
              />
            </FormGroup>
          </div>
        )}

        {(isVisible("primary language english spanish french german italian") ||
          isVisible("timezone")) &&
          (isVisible("enable agent sentiment feedback") ||
            isVisible("enable chatbot sentiment feedback")) && (
            <div
              className="border-b my-8"
              style={{ borderColor: "#CBD5E1" }}
            ></div>
          )}

        {isVisible("enable agent sentiment feedback") && (
          <div className="w-1/2">
            <FormGroup
              label="Agent Ratings"
              hint="Show thumbs up and down icons under agent messages so visitors can rate responses."
              direction="label-stacked"
            >
              <Toggle
                toggled={siteSettings.enableContactSentimentAgents}
                changeToggle={() =>
                  setSiteSettings(() => {
                    return {
                      ...siteSettings,
                      enableContactSentimentAgents:
                        !siteSettings.enableContactSentimentAgents
                    };
                  })
                }
              />
            </FormGroup>
          </div>
        )}

        {isVisible("enable chatbot sentiment feedback") && (
          <div className="w-1/2">
            <FormGroup
              label="Chatbot Ratings"
              hint="Allow visitors to rate chatbot responses with thumbs up/down icons."
              direction="label-stacked"
            >
              <Toggle
                toggled={siteSettings.enableContactSentimentBots}
                changeToggle={() =>
                  setSiteSettings(() => {
                    return {
                      ...siteSettings,
                      enableContactSentimentBots:
                        !siteSettings.enableContactSentimentBots
                    };
                  })
                }
              />
            </FormGroup>
          </div>
        )}

        {isVisible("require institutional email block personal") && (
          <div className="w-1/2">
            <FormGroup
              label="Require Institutional Email"
              hint="Only allow institutional email domains for user registration."
              direction="label-stacked"
            >
              <Toggle
                toggled={siteSettings.blockPersonalEmails}
                changeToggle={() =>
                  setSiteSettings(() => {
                    return {
                      ...siteSettings,
                      blockPersonalEmails: !siteSettings.blockPersonalEmails
                    };
                  })
                }
              />
            </FormGroup>
          </div>
        )}

        {(isVisible("enable agent sentiment feedback") ||
          isVisible("enable chatbot sentiment feedback") ||
          isVisible("require institutional email block personal")) &&
          (isVisible("software license agreement") ||
            isVisible("privacy policy")) && (
            <div
              className="border-b my-8"
              style={{ borderColor: "#CBD5E1" }}
            ></div>
          )}

        <div className="flex gap-8">
          {isVisible("software license agreement") && (
            <a
              href="https://velaro.com/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-gray-500 hover:text-blue-600 hover:underline transition-colors duration-200"
            >
              <LinkExternal01 className="w-4 h-4" />
              <span>Software License Agreement</span>
            </a>
          )}

          {isVisible("privacy policy") && (
            <a
              href="https://velaro.com/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-gray-500 hover:text-blue-600 hover:underline transition-colors duration-200"
            >
              <LinkExternal01 className="w-4 h-4" />
              <span>Privacy Policy</span>
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
